import type { ComponentProps } from 'react';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet } from 'react-native';
import { TextField } from '@sg/garnish';

// TextField wrapper for auto complete configuration.
export const AddressSearchField = (props: AddressSearchFieldProps) => {
  const { value, isRequired, shouldFocusInput, hasResults } = props;
  const { formatMessage } = useIntl();

  // ──── Invalid address notice ───────────────────────────────
  const hasTypedValidAddress = (value ?? '').length > 3;
  const isInvalidAddress = hasTypedValidAddress && !hasResults;
  const invalidNotice = formatMessage(
    messages.locationInputInvalidAddressNotice,
  );

  return (
    <TextField
      hideSubmitButton
      autoFocus={shouldFocusInput}
      style={styles.field}
      testID="address-search-autocomplete"
      autoComplete="street-address"
      clearButtonType="text"
      label={formatMessage(messages.locationAddressSearchPlaceholder)}
      clearBtnLabel={formatMessage(messages.addressSearchClear)}
      clearBtnAccessibilityHint={formatMessage(
        messages.addressSearchClearA11yHint,
      )}
      submitBtnAccessibilityLabel={formatMessage(messages.addressSearchSubmit)}
      submitAccessibilityHint={formatMessage(
        messages.addressSearchSubmitA11yHint,
      )}
      invalid={isRequired ? isInvalidAddress : undefined}
      notice={isInvalidAddress ? invalidNotice : ''}
      {...props}
    />
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  locationInputInvalidAddressNotice: {
    defaultMessage: 'Enter a valid address',
    description: 'Location | Input Invalid Address Notice',
  },
  locationAddressSearchPlaceholder: {
    defaultMessage: 'Enter your delivery address',
    description: 'Location | Address Search Placeholder',
  },
  addressSearchClear: {
    defaultMessage: 'Clear',
    description: 'Address Search | Clear',
  },
  addressSearchClearA11yHint: {
    defaultMessage: 'Clears entered text',
    description: 'Address Search | Clear A11y Hint',
  },
  addressSearchSubmit: {
    defaultMessage: 'Submit',
    description: 'Address Search | Submit',
  },
  addressSearchSubmitA11yHint: {
    defaultMessage: 'Submits entered text',
    description: 'Address Search | Submit A11y Hint',
  },
});

// ─── Styles ──────────────────────────────────────────────────

const styles = StyleSheet.create({
  field: {
    minHeight: 42,
  },
});

// ──── Types ──────────────────────────────────────────────────

type AddressSearchFieldProps = Readonly<{
  isRequired?: boolean;
  shouldFocusInput?: boolean;
  hasResults?: boolean;
}> &
  ComponentProps<typeof TextField>;
